import { useAppStore } from '@/store/useAppStore.ts'
import type { TabId } from '@/store/slices/uiSlice.ts'

const TABS: { id: TabId; label: string }[] = [
  { id: 'runtime', label: 'Runtime' },
  { id: 'scenarios', label: 'Scenarios' },
  { id: 'packBuilder', label: 'Pack Builder' },
]

// Preload cache — stores loaded modules so actual tab switch uses cached version
const preloadedModules = new Map<string, Promise<unknown>>()

function preloadTab(tabId: TabId) {
  if (preloadedModules.has(tabId)) return

  let importFn: Promise<unknown>
  switch (tabId) {
    case 'scenarios':
      importFn = import('@/components/scenarios/ScenarioTab.tsx')
      break
    case 'packBuilder':
      importFn = import('@/components/packbuilder/PackBuilderTab.tsx')
      break
    default:
      return
  }
  preloadedModules.set(tabId, importFn)
}

export function TabNav() {
  const activeTab = useAppStore((s) => s.activeTab)
  const setActiveTab = useAppStore((s) => s.setActiveTab)

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    let newIndex = index
    if (e.key === 'ArrowRight') newIndex = (index + 1) % TABS.length
    else if (e.key === 'ArrowLeft') newIndex = (index - 1 + TABS.length) % TABS.length
    else return

    e.preventDefault()
    setActiveTab(TABS[newIndex]!.id)
    const btn = e.currentTarget.parentElement?.querySelector(`[data-tab-index="${newIndex}"]`) as HTMLElement
    btn?.focus()
  }

  return (
    <nav className="tab-nav" role="tablist" aria-label="Main sections">
      {TABS.map((tab, index) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`tab-${tab.id}`}
          className={`tab-nav__btn ${activeTab === tab.id ? 'tab-nav__btn--active' : ''}`}
          aria-selected={activeTab === tab.id}
          aria-controls={`tabpanel-${tab.id}`}
          tabIndex={activeTab === tab.id ? 0 : -1}
          data-tab-index={index}
          onClick={() => setActiveTab(tab.id)}
          onMouseEnter={() => preloadTab(tab.id)}
          onKeyDown={(e) => handleKeyDown(e, index)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  )
}
